import { Anchor, Flex, Group, Text } from '@mantine/core';
import { IconAlertTriangle, IconArrowLeft } from '@tabler/icons-react';
import { useClients } from 'src/clients/hooks/useClients';
import { Client } from 'src/admin/users/types';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { ChatMode } from '../chat/types';
import { ContentMeta } from '../types';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

export type PreviousChatContext = Pick<ContentMeta, 'entityId' | 'chatMode'> & {
  clientId: string | undefined;
  url: string;
};

export function DisplacedChatBanner({
  previousChatContext,
  onGoBack,
}: {
  previousChatContext: PreviousChatContext | null;
  onGoBack: () => void;
}) {
  const { clients } = useClients();

  if (!previousChatContext?.url) return null;

  const linkLabel = buildResumeChatLinkLabel(previousChatContext, clients);

  return (
    <Flex direction="column" gap="xxs" p="sm">
      <Group gap="xxs" align="center">
        <IconAlertTriangle size={14} color="var(--mantine-color-dimmed)" stroke={1.5} aria-hidden />
        <Text size="sm" c="dimmed">
          Your next message will begin a new chat
        </Text>
      </Group>
      <Anchor component="button" type="button" size="sm" onClick={onGoBack}>
        <Group gap="xxs" align="center">
          <IconArrowLeft size={14} stroke={1.5} aria-hidden />
          <Text size="sm" fw={FONT_WEIGHTS.medium}>
            {linkLabel}
          </Text>
        </Group>
      </Anchor>
    </Flex>
  );
}

function buildResumeChatLinkLabel(
  previousChatContext: PreviousChatContext,
  clients: Client[],
): string {
  if (previousChatContext.chatMode === ChatMode.DOCUMENT) {
    return 'Return to document to resume chat';
  }
  if (previousChatContext.chatMode === ChatMode.CLIENT) {
    const previousClient = clients.find(c => c.id === previousChatContext.entityId);
    if (previousClient) {
      const name = formatName(
        previousClient.firstName,
        previousClient.lastName,
        NameFormat.NATURAL,
      );
      return `Return to ${name} to resume chat`;
    }
  }
  return 'Return to client to resume chat';
}
